import type { Product, VisaVariants, ProductList, CatalogueParams } from './api';
import { SATIS_UI as S } from './metin/satis-ui';

export function searchKey(text: string): string {
  return text.toLocaleLowerCase('tr-TR').replace(/ı/g, 'i').normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}
export function visaProducts(data: VisaVariants): Product[] {
  return data.variants.flatMap(variant => (['adult', 'child'] as const).map(traveller => ({
    slug: `${variant.code.toLowerCase().replaceAll('_', '-')}-${traveller}`,
    sourceProductSlug: 'dubai-vizesi', kind: 'VISA',
    name: S.visaProduct(variant.durationDays, variant.entries === 'MULTIPLE', traveller === 'child'),
    description: null, publishedAt: null, airline: null,
    fromPrice: variant.price[traveller] > 0 ? { amount: variant.price[traveller], currency: data.currency } : null,
  })));
}
export function isSingleVisaRequest(text: string): boolean {
  const key = searchKey(text);
  return /\b(vize\w*|visa)\b/.test(key) && /\b(dubai|bae|uae)\b/.test(key) && !/\b(otel\w*|hotel\w*|ucak\w*|flight\w*|transfer\w*|safari\w*|tur|paket\w*)\b/.test(key);
}
export function matchVisaRequest(text: string, products: Product[]): Product | undefined {
  const key = searchKey(text);
  const days = /\b(30|60)\s*gun/.exec(key)?.[1];
  const single = /tek\s*giris|single/.test(key), multiple = /cok\s*giris|multi/.test(key);
  if (!days || single === multiple || (/yetiskin/.test(key) && /cocuk/.test(key))) return undefined;
  if ([...key.matchAll(/(\d+)\s*(?:kisi|yetiskin|cocuk)/g)].some(match => Number(match[1]) !== 1)) return undefined;
  const child = /cocuk/.test(key);
  return products.find(product => {
    const name = searchKey(product.name);
    return name.includes(`${days} gun`) && name.includes(multiple ? 'cok giris' : 'tek giris') && name.includes(child ? 'cocuk' : 'yetiskin');
  });
}
export function visaMatchedNotice(product: Product): string {
  return S.visaMatched(product.name);
}
/** `matchVisaRequest` eşleştirdikten sonra talep kaydı için kural alanları (aynı metin, aynı anahtarlar). */
export function visaRequestFields(text: string): { visaDurationDays: number; entries: 'SINGLE' | 'MULTIPLE'; adultCount: number; childCount: number; childAges?: number[] } | undefined {
  const key = searchKey(text);
  const days = /\b(30|60)\s*gun/.exec(key)?.[1];
  const multiple = /cok\s*giris|multi/.test(key);
  if (!days) return undefined;
  const child = /cocuk/.test(key);
  const base = { visaDurationDays: Number(days), entries: multiple ? 'MULTIPLE' as const : 'SINGLE' as const };
  if (!child) return { ...base, adultCount: 1, childCount: 0 };
  // "5 yasinda cocuk", "cocuk 5 yas": yalniz 0-17 gecerli yas sayilir; tek cocuk icin tek yas.
  const yas = /(\d{1,2})\s*yas/.exec(key)?.[1];
  const age = yas === undefined ? undefined : Number(yas);
  return { ...base, adultCount: 0, childCount: 1, ...(age !== undefined && age <= 17 ? { childAges: [age] } : {}) };
}
export async function loadCatalogue(read: (params: CatalogueParams) => Promise<ProductList>, currency: string, cancelled: () => boolean, onPage?: (products: Product[]) => void): Promise<Product[]> {
  const products: Product[] = [];
  let offset = 0;
  while (!cancelled()) {
    const page = await read({ language: 'tr', currency, limit: 100, offset });
    if (page.page.offset !== offset || !Number.isFinite(page.page.total) || page.page.total < 0 || page.products.some(product => products.some(existing => existing.slug === product.slug))) throw new Error(S.catalogueIncomplete);
    products.push(...page.products);
    onPage?.([...products]);
    offset += page.products.length;
    if (offset >= page.page.total) return products;
    if (!page.products.length) throw new Error(S.catalogueIncomplete);
  }
  return products;
}
